// `Fragment` comes through `React.Fragment`, not a named import: bun's JSX
// transform (the test runner) injects its own `Fragment` binding for `<>` and
// refuses a second one with that name (B1026).
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { I } from './icons.jsx';
import { DOCS_HREF } from './docs-link.js';
import { DEMOS_HREF } from './demos-link.js';
import { subscribeStatus, getStatus } from '../data/socket.js';
import {
  DEFAULT_DB, getCurrentDb, subscribeCurrentDb, setCurrentDb,
  listDatabases, createDatabase, dropDatabase,
} from '../data/db-store.js';
import {
  DEFAULT_BRANCH, getCurrentBranch, subscribeCurrentBranch, setCurrentBranch,
  listBranches, createBranch, dropBranch,
} from '../data/branch-store.js';
import { useMetrics, useSqlRows, deriveRates, isHlc, hlcDeltaToMs } from '../data/use-metrics.js';

function fmtBytesShort(b) {
  if (b == null || !Number.isFinite(b)) return '—';
  if (b >= 1024 ** 4) return (b / 1024 ** 4).toFixed(1) + ' TB';
  if (b >= 1024 ** 3) return (b / 1024 ** 3).toFixed(1) + ' GB';
  if (b >= 1024 ** 2) return (b / 1024 ** 2).toFixed(0) + ' MB';
  if (b >= 1024)      return (b / 1024).toFixed(0) + ' KB';
  return Math.round(b) + ' B';
}

function fmtCountShort(n) {
  if (n == null || !Number.isFinite(n)) return '—';
  const v = Math.round(n);
  if (v >= 1_000_000_000) return (v / 1_000_000_000).toFixed(1) + 'B';
  if (v >= 1_000_000)     return (v / 1_000_000).toFixed(1) + 'M';
  if (v >= 1_000)         return (v / 1_000).toFixed(1) + 'k';
  return v.toLocaleString();
}

function fmtS3Lag(s3) {
  if (!s3) return '—';
  if (s3.lag === 0) return 'caught up';
  if (isHlc(s3.local)) {
    const ms = hlcDeltaToMs(s3.lag);
    if (ms < 1000) return `${ms.toFixed(0)} ms`;
    return `${(ms / 1000).toFixed(1)} s`;
  }
  return `${s3.lag} txn`;
}

// B05 slice 7: initials for the avatar. `root` → `RO`; `alice.smith` and
// `alice smith` → `AS`. Falls back to `?` rather than the hard-coded `AZ` that
// used to sit here, because an avatar that shows somebody else's initials is
// worse than one that admits it does not know.
function initialsOf(username) {
  if (!username) return '?';
  const parts = String(username).split(/[^A-Za-z0-9]+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

export function TopBar({ onCmdK, onToggleTheme, onNavigate, cluster, username, onLogout }) {
  // Non-interactive cluster status, click → /cluster topology page.
  // Reflects reality (`/health.peers.length` + `SHOW S3 STATUS`); no fake
  // multi-environment switcher and no node picker (clients can't pin a
  // node behind a load balancer).
  const nodeCount = cluster?.nodeCount;
  const s3Enabled = cluster?.s3Enabled;
  const unreachable = !!cluster?.unreachable;
  const summary = unreachable
    ? 'unreachable'
    : nodeCount != null
      ? `${nodeCount} node${nodeCount === 1 ? '' : 's'}${s3Enabled === true ? ' · S3 on' : s3Enabled === false ? ' · no S3' : ''}`
      : '… loading';
  // B1026: the user menu is click-toggled, not hover-revealed. The hover menu
  // sat 6px under the avatar; the pointer left `:hover` in that gap, so the
  // menu closed before it reached "Sign out". An open state, closed by a
  // click outside, by Escape, and by the sign-out itself, has no gap.
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onPointerDown = (e) => { if (!menuRef.current?.contains(e.target)) setMenuOpen(false); };
    const onKeyDown = (e) => { if (e.key === 'Escape') setMenuOpen(false); };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen]);
  return (
    <header className="topbar">
      <div className="logo">
        <img src="/kinedb-icon.svg" alt="" />
        <span>KineDB</span>
      </div>
      <button className="ctx-status" type="button" onClick={() => onNavigate?.('cluster')} title={unreachable ? `Unreachable: ${cluster?.error || 'no response'}` : 'Open cluster topology'}>
        <span className={'kdot' + (unreachable ? ' offline' : nodeCount == null ? ' orange' : '')} />
        <span className="ctx-status-text">{summary}</span>
      </button>
      <div className="spacer" />
      <button className="cmd-k" type="button" onClick={onCmdK}>
        <I.Search size={14} />
        <span>Search tables, queries, nodes…</span>
        <span className="kbd">⌘K</span>
      </button>
      <button className="icon-btn" type="button" onClick={onToggleTheme} title="Toggle theme">
        <I.Sun size={16} />
      </button>
      <button className="icon-btn" type="button" title="Notifications">
        <I.Bell size={16} /><span className="dot" />
      </button>
      <button className="icon-btn" type="button" title="Help"><I.Help size={16} /></button>
      <div className="user-menu" ref={menuRef}>
        <button
          className="avatar"
          type="button"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          aria-controls="user-menu-pop"
          title={username ? `Signed in as ${username}` : 'Not signed in'}
          onClick={() => setMenuOpen(o => !o)}
        >
          {initialsOf(username)}
        </button>
        {menuOpen && (
          <div className="user-menu-pop" id="user-menu-pop" role="menu">
            <div className="user-menu-name">{username || 'unknown'}</div>
            <button className="btn btn-ghost btn-sm" type="button" role="menuitem" onClick={() => { setMenuOpen(false); onLogout?.(); }}>
              Sign out
            </button>
          </div>
        )}
      </div>
    </header>
  );
}

export function Sidebar({ active, onNavigate, badges = {}, cluster, collapsed = false, onToggleCollapsed }) {
  // `badges` is keyed by route id; values are either a number (unseen count)
  // or `{ text, kind }`. Numbers <= 0 are hidden so the chrome stays calm
  // when nothing is happening.
  const items = [
    { group: 'Workspace' },
    { id: 'home',     label: 'Home',          icon: I.Home },
    { id: 'query',    label: 'Query',         icon: I.Query },
    { id: 'monitor',  label: 'Monitor',       icon: I.Monitor },
    { id: 'reports',  label: 'Reports',       icon: I.Reports },
    { group: 'Admin' },
    { id: 'cluster',  label: 'Cluster',       icon: I.Cluster },
    { id: 'storage',  label: 'Storage & S3',  icon: I.Storage },
    { id: 'users',    label: 'Users & roles', icon: I.Users },
    { id: 'access',   label: 'Access',        icon: I.Lock },
    { id: 'audit',    label: 'Audit log',     icon: I.Audit },
    { id: 'functions', label: 'Functions',    icon: I.Edit },
    { id: 'settings', label: 'Settings',      icon: I.Settings },
    { group: 'Help' },
    { id: 'docs',     label: 'Docs',          icon: I.Book, href: DOCS_HREF },
    { id: 'demos',    label: 'Demos',         icon: I.Play, href: DEMOS_HREF, sameTab: true },
  ];
  const renderBadge = (id) => {
    const b = badges[id];
    if (b == null || b === 0) return null;
    if (typeof b === 'number') return <span className="badge live">{b}</span>;
    if (typeof b === 'object' && b.text) return <span className={'badge ' + (b.kind || '')}>{b.text}</span>;
    return null;
  };
  return (
    <aside className={'sidebar' + (collapsed ? ' collapsed' : '')}>
      <button
        type="button"
        className="sidebar-collapse"
        onClick={() => onToggleCollapsed?.()}
        title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        <I.ChevRight size={14} style={{ transform: collapsed ? 'rotate(0deg)' : 'rotate(180deg)', transition: 'transform 120ms' }} />
      </button>
      {items.map((it, i) => it.group ? (
        collapsed ? null : <div key={i} className="nav-section-label">{it.group}</div>
      ) : it.href ? (
        // B863, B1223: the docs (dist/docs) and the demos (dist/demos) are static
        // files next to the console, not console routes, so these are plain links.
        // Docs opens a new tab and leaves the console, its socket and its query
        // tabs as they are. B1246: Demos carries `sameTab`, so it navigates this
        // tab — the owner reads the demos instead of the console, and comes back
        // with the Back button.
        <a
          key={it.id}
          className="nav-item"
          href={it.href}
          target={it.sameTab ? undefined : '_blank'}
          rel={it.sameTab ? undefined : 'noopener'}
          title={collapsed ? it.label : undefined}
        >
          <it.icon size={16} />
          {!collapsed && <span>{it.label}</span>}
        </a>
      ) : (
        <button
          key={it.id}
          type="button"
          className={'nav-item' + (active === it.id ? ' active' : '')}
          onClick={() => onNavigate(it.id)}
          title={collapsed ? it.label : undefined}
        >
          <it.icon size={16} />
          {!collapsed && <span>{it.label}</span>}
          {!collapsed && renderBadge(it.id)}
        </button>
      ))}
      {!collapsed && (
        <div className="footer">
          <div className="kpi"><span className="k">Storage</span><span className="v">{fmtBytesShort(cluster?.totalDiskBytes)}</span></div>
          {cluster?.s3Enabled && (
            <div className="kpi"><span className="k">S3 lag</span><span className="v">{fmtS3Lag(cluster.s3)}</span></div>
          )}
          <div className="kpi"><span className="k">Queries</span><span className="v">{fmtCountShort(cluster?.totalQueries)}</span></div>
          <div className="kpi"><span className="k">Slow</span><span className="v" style={{ color: cluster?.slowQueries > 0 ? 'var(--orange)' : undefined }}>{fmtCountShort(cluster?.slowQueries)}</span></div>
        </div>
      )}
    </aside>
  );
}

// B1012: one picker for the two session scopes of the status bar — the
// database (`USE <db>`, B76) and the branch (`USE BRANCH <name>`, B74). The
// store owns the selection and the SQL; this component owns the menu: list
// on open, pick, create, drop with a confirm, and the server's error text.
//
// `items` are `{name, note?}`; `note` is the small grey text at the right
// of a row (a branch's base commit). `fixed` is the item that cannot be
// dropped (`default` / `main`).
function SessionPicker({ icon, title, current, fixed, load, pick, create, drop, createLabel, placeholder, foot }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null); // null = not loaded yet
  const [err, setErr] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [confirmDrop, setConfirmDrop] = useState(null); // name pending drop
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);

  function close() {
    setOpen(false); setCreating(false); setConfirmDrop(null); setErr(null);
  }
  useEffect(() => {
    if (!open) return;
    const off = (e) => { if (ref.current && !ref.current.contains(e.target)) close(); };
    window.addEventListener('mousedown', off);
    return () => window.removeEventListener('mousedown', off);
  }, [open]);

  async function reload() {
    setErr(null);
    try { setItems(await load()); }
    catch (e) { setErr(e.message || String(e)); setItems([]); }
  }
  // Every open reloads: another session may have created or dropped one
  // since the last look, and the list is cheap.
  function toggle() {
    if (open) { close(); return; }
    setItems(null);
    setOpen(true);
    reload();
  }

  async function doPick(name) {
    if (name === current) { close(); return; }
    setBusy(true); setErr(null);
    try { await pick(name); close(); }
    catch (e) { setErr(e.message || String(e)); }
    finally { setBusy(false); }
  }
  async function doCreate() {
    const name = newName.trim();
    if (!name) return;
    setBusy(true); setErr(null);
    try {
      await create(name);
      setNewName(''); setCreating(false);
      await pick(name);
      close();
    } catch (e) { setErr(e.message || String(e)); }
    finally { setBusy(false); }
  }
  async function doDrop(name) {
    setBusy(true); setErr(null);
    try { await drop(name); setConfirmDrop(null); await reload(); }
    catch (e) { setErr(e.message || String(e)); }
    finally { setBusy(false); }
  }

  return (
    <span className="branch-picker" ref={ref} title={title}>
      <button type="button" className="branch-trigger" onClick={toggle}>
        {icon}
        <span style={{ fontFamily: 'var(--app-mono)' }}>{current}</span>
        <I.ChevDown size={10} />
      </button>
      {open && (
        <div className="branch-menu">
          {items == null && <div className="branch-menu-item disabled">Loading…</div>}
          {items && items.map((it) => (
            <div
              key={it.name}
              className={'branch-menu-item action' + (it.name === current ? ' active' : '')}
              onClick={() => doPick(it.name)}
            >
              {it.name === current
                ? <I.CheckCircle size={10} style={{ color: 'var(--cyan)' }} />
                : <span style={{ display: 'inline-block', width: 10 }} />}
              <span style={{ fontFamily: 'var(--app-mono)' }}>{it.name}</span>
              <span className="branch-menu-note">
                {it.name === current ? 'current' : it.note}
              </span>
              {it.name !== fixed && (
                <button
                  type="button"
                  className="branch-menu-x"
                  title={`Drop ${it.name}`}
                  disabled={busy}
                  onClick={(e) => { e.stopPropagation(); setConfirmDrop(it.name); }}
                >
                  <I.Close size={10} />
                </button>
              )}
            </div>
          ))}
          {confirmDrop && (
            <div className="branch-menu-foot" style={{ color: 'var(--orange)' }}>
              Drop <strong>{confirmDrop}</strong>? {foot}
              <div className="branch-menu-row">
                <button type="button" className="btn btn-sm" disabled={busy} onClick={() => doDrop(confirmDrop)}>Drop</button>
                <button type="button" className="btn btn-ghost btn-sm" disabled={busy} onClick={() => setConfirmDrop(null)}>Cancel</button>
              </div>
            </div>
          )}
          <div className="branch-menu-divider" />
          {creating ? (
            <div className="branch-menu-item" onClick={(e) => e.stopPropagation()}>
              <input
                autoFocus
                value={newName}
                placeholder={placeholder}
                className="branch-menu-input"
                disabled={busy}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') doCreate();
                  if (e.key === 'Escape') { setCreating(false); setNewName(''); }
                }}
              />
              <button type="button" className="btn btn-primary btn-sm" disabled={busy || !newName.trim()} onClick={doCreate}>Create</button>
            </div>
          ) : (
            <div className="branch-menu-item action" onClick={() => setCreating(true)}>
              <I.Plus size={10} />
              <span>{createLabel}</span>
            </div>
          )}
          {err && (
            <div className="branch-menu-foot" style={{ color: 'var(--red)' }}>{err}</div>
          )}
        </div>
      )}
    </span>
  );
}

// B74 + B1012: the session's branch. `SHOW BRANCHES` lists the branches of
// the session's database (`main` first, always); `USE BRANCH` switches the
// connection; create and drop are the two DDL statements.
function BranchPicker() {
  const [current, setCurrent] = useState(getCurrentBranch);
  useEffect(() => subscribeCurrentBranch(setCurrent), []);
  return (
    <SessionPicker
      icon={(
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <line x1="6" y1="3" x2="6" y2="15" />
          <circle cx="18" cy="6" r="3" />
          <circle cx="6" cy="18" r="3" />
          <path d="M18 9a9 9 0 0 1-9 9" />
        </svg>
      )}
      title="Current branch (of the current database)"
      current={current}
      fixed={DEFAULT_BRANCH}
      load={async () => (await listBranches()).map((b) => ({
        name: b.branch,
        note: b.base_commit && b.base_commit !== '-' ? `from ${b.base_commit}` : '',
      }))}
      pick={setCurrentBranch}
      create={createBranch}
      drop={dropBranch}
      createLabel={`New branch from ${current}…`}
      placeholder="new branch name"
      foot="The branch's own commits are unreachable after the drop."
    />
  );
}

// B76: the session's database. `SHOW DATABASES` lists them, `USE <db>`
// switches the connection (db-store re-applies it after a reconnect), and
// create / drop are the two DDL statements. Dropping a database drops every
// table in it, so the confirm says so.
function DatabasePicker() {
  const [current, setCurrent] = useState(getCurrentDb);
  useEffect(() => subscribeCurrentDb(setCurrent), []);
  return (
    <SessionPicker
      icon={<I.Database size={11} />}
      title="Current database"
      current={current}
      fixed={DEFAULT_DB}
      load={async () => (await listDatabases()).map((name) => ({ name, note: '' }))}
      pick={setCurrentDb}
      create={createDatabase}
      drop={dropDatabase}
      createLabel="New database…"
      placeholder="new database name"
      foot="Every table in it goes with it."
    />
  );
}

export function StatusBar({ cluster }) {
  const [conn, setConn] = useState(getStatus);
  useEffect(() => subscribeStatus(setConn), []);
  const connDot = { connected: '', connecting: 'connecting', offline: 'offline' }[conn] ?? 'offline';
  const connLabel = { connected: 'Connected', connecting: 'Connecting…', offline: 'Offline' }[conn] ?? 'Offline';

  // Fast-poll for per-second rates (the slow cluster summary doesn't expose
  // prev/sampledAt). 2s cadence is enough for a footer signal.
  const fast = useMetrics('', 2000);
  const rates = deriveRates(fast.latest, fast.prev, fast.sampledAt, fast.prevSampledAt) || { readsPerSec: 0, writesPerSec: 0 };

  // Raft groups + follower-state check via SHOW PEERS (leader-only). 5s poll.
  const groupsRows = useSqlRows('SHOW RAFTGROUPS', '', 5000);
  const peersRows  = useSqlRows('SHOW PEERS', '', 5000);
  const groupCount = groupsRows.rows?.length ?? null;

  // Derive "N follower lagging" — count rows whose state is not 'up'. Followers
  // viewing the page have all-null rows; filter those out before counting.
  const laggingFollowers = useMemo(() => {
    if (!peersRows.rows || !peersRows.columns) return null;
    const stIdx = peersRows.columns.indexOf('state');
    const matchIdx = peersRows.columns.indexOf('match_index');
    let lagging = 0, total = 0;
    for (const r of peersRows.rows) {
      if (r[matchIdx] == null) continue;     // skip follower-side null rows
      total++;
      if (r[stIdx] !== 'up') lagging++;
    }
    return { lagging, total };
  }, [peersRows.rows, peersRows.columns]);

  const nodeCount = cluster?.nodeCount;
  const s3 = cluster?.s3;
  const s3LagText = (() => {
    if (!cluster?.s3Enabled) return null;
    if (!s3 || s3.lag == null) return 'S3 …';
    if (s3.lag === 0) return 'S3 caught up';
    if (isHlc(s3.local)) {
      const ms = hlcDeltaToMs(s3.lag);
      if (ms < 1000) return `S3 ${ms.toFixed(0)} ms behind`;
      return `S3 ${(ms / 1000).toFixed(1)} s behind`;
    }
    return `S3 ${s3.lag} txn behind`;
  })();

  return (
    <footer className="statusbar">
      <span className="item" title={`WebSocket: ${conn}`}><span className={'dot ' + connDot} /> {connLabel}</span>
      <span className="sep" />
      <DatabasePicker />
      <span className="sep" />
      <BranchPicker />
      <span className="sep" />
      <span className="item">
        {nodeCount != null ? `${nodeCount} node${nodeCount === 1 ? '' : 's'}` : '… nodes'}
        {groupCount != null && ` · ${groupCount} RAFT group${groupCount === 1 ? '' : 's'}`}
      </span>
      {laggingFollowers && laggingFollowers.lagging > 0 && (
        <>
          <span className="sep" />
          <span className="item"><span className="dot warn" /> {laggingFollowers.lagging} of {laggingFollowers.total} follower{laggingFollowers.total === 1 ? '' : 's'} lagging</span>
        </>
      )}
      {s3LagText && (
        <>
          <span className="sep" />
          <span className="item" style={{ color: cluster?.s3?.lag > 0 ? 'var(--orange)' : undefined }}>{s3LagText}</span>
        </>
      )}
      <span className="right">
        <span className="item" title="reads / sec, derived from dml.queries_total minus routed writes">↓ {Math.round(rates.readsPerSec).toLocaleString()}/s</span>
        <span className="item" title="writes / sec, sum of calvin + direct routed totals">↑ {Math.round(rates.writesPerSec).toLocaleString()}/s</span>
      </span>
    </footer>
  );
}

export function CommandPalette({ onClose, onNavigate }) {
  const [q, setQ] = useState('');
  const [sel, setSel] = useState(0);
  const inputRef = useRef(null);
  useEffect(() => { inputRef.current?.focus(); }, []);
  const groups = [
    { label: 'Go to', items: [
      { ic: I.Home,    label: 'Home',              to: 'home',    kbd: 'G H' },
      { ic: I.Query,   label: 'Query console',     to: 'query',   kbd: 'G Q' },
      { ic: I.Monitor, label: 'Monitor',           to: 'monitor', kbd: 'G M' },
      { ic: I.Cluster, label: 'Cluster topology',  to: 'cluster', kbd: 'G C' },
      { ic: I.Users,   label: 'Users & roles',     to: 'users',   kbd: 'G U' },
      { ic: I.Lock,    label: 'Access (permissions)', to: 'access', kbd: '' },
      { ic: I.Audit,   label: 'Audit log',         to: 'audit',   kbd: '' },
      { ic: I.Edit,    label: 'Functions',         to: 'functions', kbd: '' },
    ]},
    { label: 'Actions', items: [
      { ic: I.Plus,     label: 'New query tab',          kbd: '⌘T' },
      { ic: I.Save,     label: 'Save current query',     kbd: '⌘S' },
      { ic: I.Download, label: 'Export results as CSV',  kbd: '' },
      { ic: I.Clock,    label: 'Time-travel: query as-of…', kbd: '' },
      { ic: I.Sparkles, label: 'Ask in natural language',  kbd: '' },
    ]},
  ];
  const flat = groups.flatMap(g => g.items).filter(it => !q || it.label.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="cmd-overlay" onClick={onClose}>
      <div className="cmd-palette" onClick={e => e.stopPropagation()}>
        <div className="input">
          <I.Search size={16} />
          <input
            ref={inputRef}
            placeholder="Type a command, table, or query…"
            value={q}
            onChange={e => { setQ(e.target.value); setSel(0); }}
            onKeyDown={e => {
              if (e.key === 'Escape') onClose();
              if (e.key === 'ArrowDown') { setSel(s => Math.min(flat.length - 1, s + 1)); e.preventDefault(); }
              if (e.key === 'ArrowUp')   { setSel(s => Math.max(0, s - 1)); e.preventDefault(); }
              if (e.key === 'Enter')     { const it = flat[sel]; if (it?.to) { onNavigate(it.to); onClose(); } }
            }}
          />
          <span style={{ padding: '2px 6px', background: 'var(--app-surface-3)', borderRadius: 4, fontSize: 11, fontFamily: 'var(--app-mono)', color: 'var(--app-fg3)' }}>esc</span>
        </div>
        <div className="list">
          {groups.map((g, gi) => {
            const its = g.items.filter(it => !q || it.label.toLowerCase().includes(q.toLowerCase()));
            if (!its.length) return null;
            return (
              <React.Fragment key={gi}>
                <div className="group-label">{g.label}</div>
                {its.map((it, i) => {
                  const idx = flat.indexOf(it);
                  return (
                    <div
                      key={i}
                      className={'row' + (idx === sel ? ' sel' : '')}
                      onMouseEnter={() => setSel(idx)}
                      onClick={() => { if (it.to) { onNavigate(it.to); onClose(); } }}
                    >
                      <it.ic size={15} />
                      <span>{it.label}</span>
                      {it.kbd && <span className="meta">{it.kbd}</span>}
                    </div>
                  );
                })}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
}
